/**
 * P3-42: ChallengeEffectsPanel —— 单个挑战的效果列表面板。
 *
 * 对齐 `js/challenges.js:1164-1232`：
 * - 标题为 "Effects of {challenge.label}"
 * - 仅 challenge 已解锁时列出效果
 * - 效果值使用 `game.getEffectDisplayParams` 格式化/过滤
 * - visible 需同时满足 `game.detailedChallengeInfo` 与有可见效果
 */
import { useGameTick } from "../../bridge/useGameTick";
import { t } from "../../bridge/i18n";
import type { GamePage } from "../../bridge/GameBridge";
import { SpacePanel } from "../space/SpacePanel";

interface ChallengeLike {
  name?: string;
  label?: string;
  unlocked?: boolean;
  effects?: Record<string, unknown>;
  totalEffectsCached?: Record<string, number>;
}

interface EffectItem {
  text: string;
}

interface EffectsSnapshot {
  title: string;
  items: EffectItem[];
  visible: boolean;
}

function getSnapshot(
  game: GamePage | null,
  challengeName: string,
): EffectsSnapshot | null {
  if (!game) {
    return null;
  }
  const ch = game.challenges?.getChallenge?.(challengeName) as ChallengeLike | undefined;
  const title = ch
    ? t("challendge.effects.panel.label", [ch.label ?? ch.name ?? challengeName])
    : t("challendge.effects.panel.label", [challengeName]);

  const items: EffectItem[] = [];
  if (ch?.unlocked && ch.effects) {
    for (const effectName in ch.effects) {
      const value = ch.totalEffectsCached?.[effectName] ?? 0;
      const dp = game.getEffectDisplayParams?.(effectName, value, false);
      if (dp) {
        items.push({ text: `${dp.displayEffectName}: ${dp.displayEffectValue}` });
      }
    }
  }

  return {
    title,
    items,
    visible: Boolean(game.detailedChallengeInfo) && items.length > 0,
  };
}

function snapEq(a: EffectsSnapshot | null, b: EffectsSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  if (a.title !== b.title || a.visible !== b.visible || a.items.length !== b.items.length) {
    return false;
  }
  for (let i = 0; i < a.items.length; i++) {
    if (a.items[i].text !== b.items[i].text) {
      return false;
    }
  }
  return true;
}

export default function ChallengeEffectsPanel({ challengeName }: { challengeName: string }) {
  const snapshot = useGameTick(
    (game) => getSnapshot(game as GamePage | null, challengeName),
    snapEq,
  );

  if (!snapshot || !snapshot.visible) {
    return null;
  }

  return (
    <SpacePanel title={snapshot.title}>
      <ul style={{ marginTop: 0, marginBottom: 0 }}>
        {snapshot.items.map((item) => (
          <li key={item.text} dangerouslySetInnerHTML={{ __html: item.text }} />
        ))}
      </ul>
    </SpacePanel>
  );
}
